import {
  call,
  delay,
  put,
  race,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import type { PayloadAction } from 'typesafe-actions';

import type {
  GetListedCustomersRequestData,
  ListedCustomersListViewPage,
  PersistCustomerData,
} from '@payaca/types/listedCustomerTypes';
import type { PatchCustomer, SagaConfig } from './customerTypes';
import { ActionType } from './customerTypes';

import { Req } from '@payaca/helpers/storeHelper';
import type { Customer } from '@payaca/types/customerTypes';
import { refreshAuthToken } from '../auth/refreshAuthToken';
import { DEFAULT_API_REQUEST_TIMEOUT_MS } from '../constants';
import { handleAsyncAction } from '../utils';
import {
  clearCurrentCustomer,
  clearCustomers,
  clearListedCustomersPage,
  getCustomerFailure,
  getCustomerSuccess,
  getListedCustomersPageFailure,
  getListedCustomersPageSuccess,
  patchCustomer,
  persistCustomerFailure,
  persistCustomerSuccess,
  requestGetCustomer,
  setCurrentCustomer,
} from './customerActions';

const customerSagaCreator = ({
  apiBaseurl,
  getAuthHeader,
  isNativeApp,
}: SagaConfig) => {
  const req = Req(`${apiBaseurl}/api`, getAuthHeader, isNativeApp);
  const providerReq = Req(
    `${apiBaseurl}/provider/rest`,
    getAuthHeader,
    isNativeApp
  );

  function* handlePersistCustomer(
    action: PayloadAction<
      ActionType.REQUEST_PERSIST_CUSTOMER,
      {
        persistCustomerData: PersistCustomerData;
        onPersistSuccess?: (customerId: number) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(persistCustomer, action.payload.persistCustomerData),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          persistCustomerFailure(new Error('Persist customer timed out.'))
        );
      } else {
        // oxlint-disable-next-line eslint/no-unused-expressions -- FIXME: pre-existing error: Expected expression to be used
        action.payload.onPersistSuccess &&
          action.payload.onPersistSuccess(response);
        yield put(persistCustomerSuccess());
      }
    } catch (error: any) {
      yield put(persistCustomerFailure(error));
    }
  }

  function* handleGetCustomer(
    action: PayloadAction<
      ActionType.REQUEST_GET_CUSTOMER,
      {
        customerId: number;
        callback: (customer: Customer) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(getCustomer, action.payload.customerId),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          getCustomerFailure(
            action.payload.customerId,
            new Error('Get customer timed out.')
          )
        );
      } else {
        yield put(getCustomerSuccess(response));
        // oxlint-disable-next-line eslint/no-unused-expressions -- FIXME: pre-existing error: Expected expression to be used
        action.payload.callback && action.payload.callback(response);
      }
    } catch (error: any) {
      yield put(getCustomerFailure(action.payload.customerId, error));
    }
  }

  const persistCustomer = async (persistCustomerData: PersistCustomerData) => {
    const authHeader = await getAuthHeader();

    return fetch(`${apiBaseurl}/api/customer`, {
      method: 'POST',
      body: JSON.stringify(persistCustomerData),
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        'X-Simple-Job': 'true',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `Persist customer failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  function* handleGetListedCustomersPage(
    action: PayloadAction<
      ActionType.REQUEST_GET_LISTED_CUSTOMERS_PAGE,
      {
        getListedCustomersRequestData: GetListedCustomersRequestData;
        callback?: (listedCustomersPage: ListedCustomersListViewPage) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);

    try {
      const { response, timeout } = yield race({
        response: call(
          getListedCustomersPage,
          action.payload.getListedCustomersRequestData
        ),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          getListedCustomersPageFailure(
            new Error('Get listed customers request timed out.')
          )
        );
      } else {
        action.payload.callback?.(response);
        yield put(getListedCustomersPageSuccess(response));
      }
    } catch (error: any) {
      yield put(getListedCustomersPageFailure(error));
    }
  }

  const getListedCustomersPage = async (
    getListedCustomersRequestData: GetListedCustomersRequestData
  ) => {
    return fetch(
      `${apiBaseurl}/api/listed_customers?pageNumber=${
        getListedCustomersRequestData.pageNumber
      }&pageSize=${getListedCustomersRequestData.pageSize}&searchTerm=${
        getListedCustomersRequestData.searchTerm || ''
      }`,
      {
        method: 'GET',
        headers: {
          Authorization: await getAuthHeader(),
          'Content-Type': 'application/json',
          'X-Simple-Job': 'true',
        },
      }
    ).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `GetListedCustomersPage failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  const getCustomer = async (customerId: number) => {
    const authHeader = await getAuthHeader();

    return fetch(`${apiBaseurl}/api/customer/${customerId}`, {
      method: 'GET',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        'X-Simple-Job': 'true',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `Get customer failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  function* handleRequestGetAndSetCurrentCustomer(
    action: PayloadAction<
      ActionType.REQUEST_GET_AND_SET_CURRENT_CUSTOMER,
      {
        customerId: number;
      }
    >
  ): any {
    yield put(requestGetCustomer(action.payload.customerId));
    const getCustomerResult = yield take([
      ActionType.GET_CUSTOMER_SUCCESS,
      ActionType.GET_CUSTOMER_FAILURE,
    ]);

    if (getCustomerResult.type === ActionType.GET_CUSTOMER_SUCCESS) {
      yield put(setCurrentCustomer(getCustomerResult.payload.customer));
    }
  }

  const handlePatchCustomer = handleAsyncAction<PatchCustomer>(
    patchCustomer,
    async (payload) => {
      const response = await req.patch(
        `/customer/${payload.customerId}`,
        payload.body
      );
      return response.json();
    },
    (data, payload) => {
      payload.payload?.callback?.(data);
    }
  );

  function* handleLogout() {
    yield put(clearCurrentCustomer());
    yield put(clearListedCustomersPage());
    yield put(clearCustomers());
  }

  return function* () {
    yield takeEvery(ActionType.REQUEST_PERSIST_CUSTOMER, handlePersistCustomer);
    yield takeEvery(ActionType.REQUEST_GET_CUSTOMER, handleGetCustomer);
    yield takeLatest(
      ActionType.REQUEST_GET_AND_SET_CURRENT_CUSTOMER,
      handleRequestGetAndSetCurrentCustomer
    );
    yield takeLatest(
      ActionType.REQUEST_GET_LISTED_CUSTOMERS_PAGE,
      handleGetListedCustomersPage
    );
    yield takeEvery(ActionType.PATCH_CUSTOMER_REQUEST, handlePatchCustomer);

    yield takeEvery('auth.logout', handleLogout);
  };
};

export default customerSagaCreator;
